import { useEffect, useMemo, useRef, useState } from 'react';
import { Paper, Table, NumberInput, Text, Group, Stack, Badge } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import dayjs from 'dayjs';
import { addWorkdays, countWorkdays, fromISODate, toISODate } from './workdays';
import type { QuotationSchedule } from './NewQuotationModal';
import { IconTimer } from '../../components/icons/CncIcons';

export default function OrderDurationSection({
  schedule,
  onChange,
  disabled,
}: {
  schedule: QuotationSchedule;
  onChange: (next: QuotationSchedule) => void;
  disabled?: boolean;
}) {
  // Local mirror of the schedule for interactive editing. Any field the user edits is pushed
  // back via `onChange` (debounced) so the parent persists to the server.
  const [s, setS] = useState<QuotationSchedule>(schedule);
  useEffect(() => { setS(schedule); }, [schedule]);

  // Debounced persist — any local change flushes 400 ms after the last edit.
  const saveTimer = useRef<number | null>(null);
  function patch(p: Partial<QuotationSchedule>) {
    const next = { ...s, ...p };
    setS(next);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => onChange(next), 400);
  }

  // Derived end dates / starts. Subsequent steps' effective start = max(user-override, previous-step-end).
  const d = useMemo(() => {
    const startDate = fromISODate(s.startDate);
    // Supplying Consumables
    const supStart = startDate;
    const supDays = s.supplyDays ?? null;
    const supEnd = supStart && supDays != null ? addWorkdays(supStart, supDays) : null;
    // In Execution
    const exUser = fromISODate(s.executionStartDate);
    const exStart = supEnd ? (exUser && exUser > supEnd ? exUser : supEnd) : null;
    const exDays = s.executionDays ?? null;
    const exEnd = exStart && exDays != null ? addWorkdays(exStart, exDays) : null;
    // Preparing to Ship
    const prepUser = fromISODate(s.prepStartDate);
    const prepStart = exEnd ? (prepUser && prepUser > exEnd ? prepUser : exEnd) : null;
    const prepDays = s.prepDays ?? null;
    const prepEnd = prepStart && prepDays != null ? addWorkdays(prepStart, prepDays) : null;
    // Shipping
    const shipUser = fromISODate(s.shippingStartDate);
    const shipStart = prepEnd ? (shipUser && shipUser > prepEnd ? shipUser : prepEnd) : null;
    const shipDays = s.shippingDays ?? null;
    const shipEnd = shipStart && shipDays != null ? addWorkdays(shipStart, shipDays) : null;
    return {
      startDate, supStart, supEnd, supDays,
      exStart, exEnd, exDays,
      prepStart, prepEnd, prepDays,
      shipStart, shipEnd, shipDays,
    };
  }, [s]);

  // Updating the days fields validates mins; updating end dates back-computes days.
  function updateDaysByEndDate(
    field: 'supplyDays' | 'executionDays' | 'prepDays' | 'shippingDays',
    stepStart: Date | null,
    end: Date | null,
    min: number,
  ) {
    if (!stepStart || !end) return;
    const count = Math.max(min, countWorkdays(stepStart, end));
    patch({ [field]: count } as Partial<QuotationSchedule>);
  }

  // Summary figures
  const orderStart = d.startDate ? dayjs(d.startDate).format('YYYY-MM-DD') : '—';
  const shippingDay = d.shipStart ? dayjs(d.shipStart).format('YYYY-MM-DD') : '—';
  const approxShipping = d.shipDays != null ? `${d.shipDays} day${d.shipDays === 1 ? '' : 's'}` : '—';
  const clientReceiving = d.shipEnd ? dayjs(d.shipEnd).format('YYYY-MM-DD') : '—';

  return (
    <div className="section">
      <div className="section-header">
        <Group gap={8} wrap="nowrap"><IconTimer size={16} /><span>Order Duration</span></Group>
      </div>
      <div className="section-body">
        <Paper withBorder>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 220 }}>Order Step</Table.Th>
                <Table.Th style={{ width: 140 }}>Start date</Table.Th>
                <Table.Th style={{ width: 100 }}>Work days</Table.Th>
                <Table.Th style={{ width: 140 }}>End date</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {/* Order Step: Start — only a start date, anchor for the schedule. */}
              <Table.Tr>
                <Table.Td><Badge variant="light">Start</Badge></Table.Td>
                <Table.Td>
                  <DateInput
                    data-tour="sched-start"
                    value={d.startDate}
                    onChange={(v) => patch({ startDate: v ? toISODate(v) : null })}
                    disabled={disabled}
                    valueFormat="YYYY-MM-DD"
                  />
                </Table.Td>
                <Table.Td><Text c="dimmed" size="sm">—</Text></Table.Td>
                <Table.Td><Text c="dimmed" size="sm">anchor</Text></Table.Td>
              </Table.Tr>

              {/* Supplying Consumables — start = Order Start (read-only). Min 0 days. */}
              <Table.Tr>
                <Table.Td><Badge variant="light" color="gray">Supplying Consumables</Badge></Table.Td>
                <Table.Td>
                  <DateInput value={d.supStart} disabled valueFormat="YYYY-MM-DD" />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    data-tour="sched-supply-days"
                    value={d.supDays ?? ''}
                    onChange={(v) => {
                      const n = v === '' || v === undefined ? null : Math.max(0, Math.round(Number(v)));
                      patch({ supplyDays: n });
                    }}
                    min={0}
                    step={1}
                    disabled={disabled || !d.supStart}
                  />
                </Table.Td>
                <Table.Td>
                  <DateInput
                    value={d.supEnd}
                    onChange={(v) => updateDaysByEndDate('supplyDays', d.supStart, v, 0)}
                    minDate={d.supStart ?? undefined}
                    disabled={disabled || !d.supStart}
                    valueFormat="YYYY-MM-DD"
                  />
                </Table.Td>
              </Table.Tr>

              {/* In Execution — start ≥ Supplying end; min 1 day. */}
              <Table.Tr>
                <Table.Td><Badge variant="light" color="orange">In Execution</Badge></Table.Td>
                <Table.Td>
                  <DateInput
                    value={d.exStart}
                    onChange={(v) => patch({ executionStartDate: v ? toISODate(v) : null })}
                    minDate={d.supEnd ?? undefined}
                    disabled={disabled || !d.supEnd}
                    valueFormat="YYYY-MM-DD"
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    data-tour="sched-exec-days"
                    value={d.exDays ?? ''}
                    onChange={(v) => {
                      const n = v === '' || v === undefined ? null : Math.max(1, Math.round(Number(v)));
                      patch({ executionDays: n });
                    }}
                    min={1}
                    step={1}
                    disabled={disabled || !d.exStart}
                  />
                </Table.Td>
                <Table.Td>
                  <DateInput
                    value={d.exEnd}
                    onChange={(v) => updateDaysByEndDate('executionDays', d.exStart, v, 1)}
                    minDate={d.exStart ?? undefined}
                    disabled={disabled || !d.exStart}
                    valueFormat="YYYY-MM-DD"
                  />
                </Table.Td>
              </Table.Tr>

              {/* Preparing to Ship — start ≥ Execution end; min 0. */}
              <Table.Tr>
                <Table.Td><Badge variant="light" color="blue">Preparing to Ship</Badge></Table.Td>
                <Table.Td>
                  <DateInput
                    value={d.prepStart}
                    onChange={(v) => patch({ prepStartDate: v ? toISODate(v) : null })}
                    minDate={d.exEnd ?? undefined}
                    disabled={disabled || !d.exEnd}
                    valueFormat="YYYY-MM-DD"
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    data-tour="sched-prep-days"
                    value={d.prepDays ?? ''}
                    onChange={(v) => {
                      const n = v === '' || v === undefined ? null : Math.max(0, Math.round(Number(v)));
                      patch({ prepDays: n });
                    }}
                    min={0}
                    step={1}
                    disabled={disabled || !d.prepStart}
                  />
                </Table.Td>
                <Table.Td>
                  <DateInput
                    value={d.prepEnd}
                    onChange={(v) => updateDaysByEndDate('prepDays', d.prepStart, v, 0)}
                    minDate={d.prepStart ?? undefined}
                    disabled={disabled || !d.prepStart}
                    valueFormat="YYYY-MM-DD"
                  />
                </Table.Td>
              </Table.Tr>

              {/* Shipping — start ≥ Preparing end; min 0. */}
              <Table.Tr>
                <Table.Td><Badge variant="light" color="cyan">Shipping</Badge></Table.Td>
                <Table.Td>
                  <DateInput
                    value={d.shipStart}
                    onChange={(v) => patch({ shippingStartDate: v ? toISODate(v) : null })}
                    minDate={d.prepEnd ?? undefined}
                    disabled={disabled || !d.prepEnd}
                    valueFormat="YYYY-MM-DD"
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    data-tour="sched-ship-days"
                    value={d.shipDays ?? ''}
                    onChange={(v) => {
                      const n = v === '' || v === undefined ? null : Math.max(0, Math.round(Number(v)));
                      patch({ shippingDays: n });
                    }}
                    min={0}
                    step={1}
                    disabled={disabled || !d.shipStart}
                  />
                </Table.Td>
                <Table.Td>
                  <DateInput
                    value={d.shipEnd}
                    onChange={(v) => updateDaysByEndDate('shippingDays', d.shipStart, v, 0)}
                    minDate={d.shipStart ?? undefined}
                    disabled={disabled || !d.shipStart}
                    valueFormat="YYYY-MM-DD"
                  />
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Paper>

        <Stack gap={2} mt="xs">
          <Group gap="lg" wrap="wrap">
            <Text size="sm"><b>Order start:</b> {orderStart}</Text>
            <Text size="sm"><b>Shipping day:</b> {shippingDay}</Text>
            <Text size="sm"><b>Approx shipping:</b> {approxShipping}</Text>
            <Text size="sm"><b>Client receives:</b> {clientReceiving}</Text>
          </Group>
          <Text size="xs" c="dimmed">Weekends are excluded from the work-day counts.</Text>
        </Stack>
      </div>
    </div>
  );
}
